import { useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { UI, DISPLAY } from '../styles/tl5';
import { useSubmitPartnershipInquiryMutation } from '../store/siteContentApi';
// The reference's Franchise hero is byte-identical to the About centre
// photograph (same md5), so it reuses that asset.
import heroImg from '../assets/img/v5/about-centre.jpg';

// Franchise — "Open a TrustLab Care Centre", built to the v5 reference.
//
// Five sections: hero, what you get, good fit / what you'll need,
// from enquiry to opening, and the application form.
//
// Three notes on the form, all forced by POST /partnerships as it stands
// (see API-NOTES 2.12 and 2.21):
//
//   · The reference asks for name, mobile, city and area. The controller
//     rejects a submission with no email (400), so Email is a fifth
//     field. Without it every franchise enquiry would fail silently.
//   · `partnership_type` is an enum and the controller normalises free
//     text into it. Every submission here is sent as a franchise lead,
//     because that is what this page is for — left to the normaliser,
//     "Pharmacy" and "Diagnostic centre" would both fall through to
//     `other` and the franchise pipeline would be indistinguishable from
//     the partner-segment leads.
//   · There is no column for the area or for what the applicant runs
//     today, so both go into `message` as labelled lines rather than
//     being dropped — the partnerships team needs them to plan a site
//     visit.

const PERKS = [
  ['TrustLab brand and fit-out', 'Signage, chair, fridge and furniture to the Care Centre standard.', '#FFFFFF', 'var(--tl-g9)'],
  ['Training and certification', 'Phlebotomy, sample handling and patient care for your staff.', 'var(--tl-teal)', '#FFFFFF'],
  ['Daily sample pickup', 'Cold-chain logistics to our central lab, every day.', 'var(--tl-yellow)', 'var(--tl-g9)'],
  ['Booking and LIS software', 'Billing, barcodes and reports in one system.', 'var(--tl-g9)', '#FFFFFF'],
  ['Home collection leads', 'Bookings from your area routed to your centre.', '#FFFFFF', 'var(--tl-g9)'],
  ['2,000+ tests to offer', 'Including specialty and genomics, run in our central lab.', '#D5EFE8', 'var(--tl-g9)'],
].map(([t, d, bg, fg]) => ({ t, d, bg, fg }));

const GOOD_FIT = ['A clinic', 'A pharmacy', 'A diagnostic centre', 'Or want to start one'];

// The reference ships these four values as unfilled placeholders —
// "[x] sq ft", "₹ [amount]". They are commercial terms only the business
// can set, so the rows say where the number comes from instead of
// carrying an invented one. API-NOTES 2.21.
const REQUIREMENTS = [
  ['Space', 'Ground floor, street access'],
  ['Investment', 'Quoted for your city'],
  ['Staff', 'Trained phlebotomists'],
  ['Time to open', 'Set at the site visit'],
];

const STEPS = ['Enquiry and call', 'Site visit', 'Agreement', 'Fit-out and training', 'Open for patients'];

const RUNS = ['Clinic', 'Pharmacy', 'Diagnostic centre', 'New business'];

const EMPTY = { name: '', phone: '', email: '', city: '', area: '' };

// ── shared fragments ──────────────────────────────────────────────────
const card = {
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
  minWidth: 0,
  p: 'clamp(20px, 3.4vw, 32px)',
  borderRadius: '28px',
  bgcolor: '#FFFFFF',
};

const h2 = {
  m: 0,
  fontFamily: DISPLAY,
  fontWeight: 700,
  fontSize: 'clamp(32px, 4vw, 48px)',
  lineHeight: 1.05,
  letterSpacing: '-.02em',
  color: 'var(--tl-g9)',
};

// The card headings are a size down from the section headings.
const h3 = {
  m: 0,
  fontFamily: DISPLAY,
  fontWeight: 700,
  fontSize: 'clamp(26px, 3vw, 36px)',
  lineHeight: 1.05,
  letterSpacing: '-.02em',
};

const field = {
  border: '1.5px solid var(--tl-line)',
  borderRadius: '14px',
  outline: 0,
  font: 'inherit',
  fontFamily: UI,
  fontSize: 16,
  p: '13px 14px',
  color: 'var(--tl-g9)',
  bgcolor: '#FFFFFF',
  width: '100%',
  minWidth: 0,
  '&:focus': { borderColor: 'var(--tl-teal)' },
};

const fieldLabel = {
  display: 'flex',
  flexDirection: 'column',
  gap: 0.75,
  minWidth: 0,
  fontFamily: UI,
  fontSize: 13,
  fontWeight: 700,
  color: 'var(--tl-muted-2)',
};

const Franchise = () => {
  const [submit, { isLoading: sending }] = useSubmitPartnershipInquiryMutation();

  const [form, setForm] = useState(EMPTY);
  const [runs, setRuns] = useState(RUNS[0]);
  const [error, setError] = useState('');
  const [sent, setSent] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return setError('Please enter your name');
    if (form.phone.replace(/\D/g, '').length < 10) return setError('Please enter a 10-digit mobile number');
    if (!/^\S+@\S+\.\S+$/.test(form.email.trim())) return setError('Please enter an email address we can reach you on');
    if (!form.city.trim()) return setError('Please enter your city');
    setError('');
    try {
      await submit({
        name: form.name.trim(),
        phone: form.phone.trim(),
        email: form.email.trim(),
        city: form.city.trim(),
        partnership_type: 'master_franchise',
        message: [
          'Applying for: TrustLab Care Centre franchise',
          `Currently runs: ${runs}`,
          form.area.trim() ? `Area / locality: ${form.area.trim()}` : null,
        ].filter(Boolean).join('\n'),
      }).unwrap();
      setSent(true);
      setForm(EMPTY);
    } catch (err) {
      setError(err?.data?.message || 'Could not send — please try again.');
    }
    return undefined;
  };

  return (
    <Box>
      {/* ── 1. hero ──────────────────────────────────────────────────── */}
      <Box component="section" sx={{ pt: 2, px: 2 }}>
        <Box
          sx={{
            position: 'relative',
            width: '100%',
            maxWidth: 1320,
            mx: 'auto',
            minHeight: { xs: 380, md: 520 },
            display: 'flex',
            alignItems: 'end',
            borderRadius: '36px',
            overflow: 'hidden',
            bgcolor: 'var(--tl-g9)',
          }}
        >
          <Box
            component="img"
            src={heroImg}
            alt=""
            sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }}
          />
          <Box
            aria-hidden="true"
            sx={{
              position: 'absolute',
              inset: 0,
              background:
                'linear-gradient(90deg, rgba(7,33,29,0.92) 0%, rgba(7,33,29,0.72) 50%, rgba(7,33,29,0.2) 100%)',
            }}
          />
          <Box
            sx={{
              position: 'relative',
              display: 'flex',
              flexDirection: 'column',
              gap: 2.25,
              maxWidth: 880,
              width: '100%',
              p: 'clamp(28px, 5vw, 56px)',
              color: '#FFFFFF',
            }}
          >
            <Box
              component="span"
              sx={{
                alignSelf: 'start',
                px: '10px',
                py: '6px',
                borderRadius: '999px',
                bgcolor: 'var(--tl-yellow)',
                color: 'var(--tl-g9)',
                fontFamily: UI,
                fontSize: 12,
                fontWeight: 800,
              }}
            >
              Partner with TrustLab
            </Box>
            <Typography
              component="h1"
              sx={{
                m: 0,
                fontFamily: DISPLAY,
                fontWeight: 700,
                fontSize: 'clamp(34px, 5.4vw, 72px)',
                lineHeight: 0.95,
                letterSpacing: '-.035em',
                textWrap: 'balance',
                color: '#FFFFFF',
              }}
            >
              Open a TrustLab Care Centre.
            </Typography>
            <Typography
              sx={{ m: 0, fontFamily: UI, fontSize: 18, lineHeight: 1.55, color: '#D5E7E2', maxWidth: 600 }}
            >
              Run a trusted sample collection centre in your area, backed by our
              NABL-accredited central lab.
            </Typography>
            {/* An in-page anchor, so it needs a plain <a>, not a Link. */}
            <Box
              component="a"
              href="#apply"
              sx={{
                alignSelf: 'start',
                borderRadius: '16px',
                p: '15px 22px',
                bgcolor: 'var(--tl-yellow)',
                color: 'var(--tl-g9)',
                fontFamily: UI,
                fontSize: 16,
                fontWeight: 800,
                textDecoration: 'none',
                '&:hover': { bgcolor: 'var(--tl-yellow-d)' },
              }}
            >
              Apply to partner
            </Box>
          </Box>
        </Box>
      </Box>

      <Box
        component="main"
        sx={{ width: '100%', maxWidth: 1320, mx: 'auto', px: 2, pt: 1.5, pb: 9, display: 'flex', flexDirection: 'column' }}
      >
        {/* ── 2. what you get ────────────────────────────────────────── */}
        <Box component="section" sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 5 }}>
          <Typography component="h2" sx={{ ...h2, px: 1 }}>What you get</Typography>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 260px), 1fr))',
              gap: 1.5,
            }}
          >
            {PERKS.map((p) => (
              <Box
                key={p.t}
                component="section"
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1.25,
                  minHeight: 180,
                  p: '26px',
                  borderRadius: '28px',
                  bgcolor: p.bg,
                  color: p.fg,
                }}
              >
                <Box
                  component="span"
                  sx={{ fontFamily: DISPLAY, fontWeight: 700, fontSize: 22, lineHeight: 1.15, letterSpacing: '-.01em' }}
                >
                  {p.t}
                </Box>
                <Box component="span" sx={{ fontFamily: UI, fontSize: 15, lineHeight: 1.55, opacity: 0.85 }}>
                  {p.d}
                </Box>
              </Box>
            ))}
          </Box>
        </Box>

        {/* ── 3. good fit / what you'll need ─────────────────────────── */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', gap: 1.5, mt: 4.5 }}>
          <Box component="section" sx={{ ...card, flex: '1 1 360px' }}>
            <Box component="span" sx={{ ...h3, color: 'var(--tl-g9)' }}>Good fit if you run</Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {GOOD_FIT.map((g) => (
                <Box
                  key={g}
                  component="span"
                  sx={{
                    p: '10px 14px',
                    borderRadius: '999px',
                    bgcolor: 'var(--tl-mint)',
                    color: 'var(--tl-g9)',
                    fontFamily: UI,
                    fontSize: 14,
                    fontWeight: 700,
                  }}
                >
                  {g}
                </Box>
              ))}
            </Box>
          </Box>

          <Box component="section" sx={{ ...card, flex: '1 1 360px', bgcolor: 'var(--tl-g9)', color: '#FFFFFF' }}>
            <Box component="span" sx={{ ...h3, color: '#FFFFFF' }}>What you&rsquo;ll need</Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, fontSize: 15 }}>
              {REQUIREMENTS.map(([k, v]) => (
                <Box
                  key={k}
                  component="span"
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    gap: 1.5,
                    pt: 1.25,
                    borderTop: '1px solid var(--tl-g6)',
                    fontFamily: UI,
                    fontSize: 15,
                  }}
                >
                  <Box component="span" sx={{ color: '#9FC2BA' }}>{k}</Box>
                  <Box component="span" sx={{ fontWeight: 800, textAlign: 'right' }}>{v}</Box>
                </Box>
              ))}
            </Box>
            <Box component="span" sx={{ fontFamily: UI, fontSize: 14, lineHeight: 1.6, color: '#9FC2BA' }}>
              Space, investment and opening time depend on your city and the site.
              We go through the numbers on the call.
            </Box>
          </Box>
        </Box>

        {/* ── 4. from enquiry to opening ─────────────────────────────── */}
        <Box component="section" sx={{ ...card, mt: 4.5 }}>
          <Box component="span" sx={{ ...h3, color: 'var(--tl-g9)' }}>From enquiry to opening</Box>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 180px), 1fr))',
              gap: 1.5,
            }}
          >
            {STEPS.map((t, i) => (
              <Box
                key={t}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1,
                  p: '20px',
                  borderRadius: '22px',
                  bgcolor: '#F6FAF8',
                }}
              >
                <Box component="span" sx={{ fontFamily: DISPLAY, fontWeight: 700, fontSize: 32, color: 'var(--tl-teal)' }}>
                  {`0${i + 1}`}
                </Box>
                <Box component="span" sx={{ fontFamily: UI, fontWeight: 800, fontSize: 16, color: 'var(--tl-g9)' }}>
                  {t}
                </Box>
              </Box>
            ))}
          </Box>
        </Box>

        {/* ── 5. apply ───────────────────────────────────────────────── */}
        <Box
          component="section"
          id="apply"
          sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', gap: 1.5, mt: 4.5, scrollMarginTop: '100px' }}
        >
          <Box sx={{ flex: '1 1 340px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 1.75, p: 1 }}>
            <Typography component="h2" sx={h2}>Apply to partner</Typography>
            <Typography sx={{ m: 0, fontFamily: UI, fontSize: 16, lineHeight: 1.65, color: 'var(--tl-muted)' }}>
              Our partnerships team will call you within two working days.
            </Typography>
          </Box>

          <Box component="section" sx={{ ...card, flex: '1.4 1 440px' }}>
            {sent ? (
              <>
                <Box
                  component="span"
                  aria-hidden="true"
                  sx={{
                    width: 52,
                    height: 52,
                    borderRadius: '50%',
                    bgcolor: 'var(--tl-teal)',
                    color: '#FFFFFF',
                    display: 'grid',
                    placeItems: 'center',
                    fontSize: 24,
                    fontWeight: 800,
                  }}
                >
                  ✓
                </Box>
                <Box component="span" sx={{ ...h3, color: 'var(--tl-g9)' }}>Application received.</Box>
                <Box component="span" sx={{ fontFamily: UI, fontSize: 16, lineHeight: 1.65, color: 'var(--tl-muted)' }}>
                  We&rsquo;ll call you within two working days to discuss your location.
                </Box>
              </>
            ) : (
              <Box component="form" onSubmit={onSubmit} noValidate sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 200px), 1fr))',
                    gap: 1.25,
                  }}
                >
                  <Box component="label" sx={fieldLabel}>
                    Full name
                    <Box component="input" value={form.name} onChange={set('name')} sx={field} />
                  </Box>
                  <Box component="label" sx={fieldLabel}>
                    Mobile
                    <Box component="input" value={form.phone} onChange={set('phone')} placeholder="+91" inputMode="tel" sx={field} />
                  </Box>
                  {/* Not in the reference. POST /partnerships rejects a
                      submission with no email, so without this the form
                      could never succeed. */}
                  <Box component="label" sx={fieldLabel}>
                    Email
                    <Box component="input" value={form.email} onChange={set('email')} placeholder="name@email.com" type="email" sx={field} />
                  </Box>
                  <Box component="label" sx={fieldLabel}>
                    City
                    <Box component="input" value={form.city} onChange={set('city')} sx={field} />
                  </Box>
                  <Box component="label" sx={fieldLabel}>
                    Area / locality
                    <Box component="input" value={form.area} onChange={set('area')} sx={field} />
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Box component="span" sx={{ fontFamily: UI, fontSize: 13, fontWeight: 700, color: 'var(--tl-muted-2)' }}>
                    You currently run
                  </Box>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                    {RUNS.map((r) => {
                      const on = r === runs;
                      return (
                        <Box
                          key={r}
                          component="button"
                          type="button"
                          onClick={() => setRuns(r)}
                          aria-pressed={on}
                          sx={{
                            border: 0,
                            cursor: 'pointer',
                            borderRadius: '999px',
                            p: '10px 16px',
                            whiteSpace: 'nowrap',
                            fontFamily: UI,
                            fontSize: 14,
                            fontWeight: 700,
                            bgcolor: on ? 'var(--tl-g9)' : 'var(--tl-mint)',
                            color: on ? '#FFFFFF' : 'var(--tl-g9)',
                          }}
                        >
                          {r}
                        </Box>
                      );
                    })}
                  </Box>
                </Box>

                {error && (
                  <Box component="span" role="alert" sx={{ fontFamily: UI, fontSize: 14, fontWeight: 700, color: '#B23B37' }}>
                    {error}
                  </Box>
                )}

                <Box
                  component="button"
                  type="submit"
                  disabled={sending}
                  sx={{
                    alignSelf: 'start',
                    border: 0,
                    cursor: sending ? 'default' : 'pointer',
                    borderRadius: '16px',
                    p: '15px 22px',
                    bgcolor: sending ? '#B9C9C4' : 'var(--tl-g9)',
                    color: '#FFFFFF',
                    fontFamily: UI,
                    fontSize: 16,
                    fontWeight: 800,
                  }}
                >
                  {sending ? 'Sending…' : 'Submit application'}
                </Box>
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default Franchise;
